import { FatoStateColor, VERTIPORT_DIAMETER_SCALE } from '@components/map/layers/vertiports/vertiport.constants';
import useSuperCluster from '@hooks/useSuperCluster';
import { GeoJsonProperties } from 'geojson';
import { useEffect, useRef } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import Supercluster, { PointFeature } from 'supercluster';

import { FatoStateEnum } from '@/types/intercom/intercom.types';

type VertiportClusterProps = {
  NOMINAL: number;
  INOPERABLE: number;
  CONTINGENCY: number;
  imageId: string;
};

const createPieByPoints = (
  props: { NOMINAL: number; INOPERABLE: number; CONTINGENCY: number; TOTAL: number },
  diameter = VERTIPORT_DIAMETER_SCALE,
) => {
  const total = props.TOTAL || 1;
  const radius = diameter / 2;

  const slices: string[] = [];
  const points: { key: keyof typeof FatoStateColor }[] = Object.values(FatoStateEnum).flatMap((key) =>
    new Array(props[key] || 0).fill(null).map(() => ({ key })),
  );

  const anglePerSlice = (2 * Math.PI) / total;
  for (let index = 0; index < points.length; index++) {
    const point = points[index];
    const startAngle = index * anglePerSlice;
    const endAngle = startAngle + anglePerSlice;

    const x1 = radius + radius * Math.sin(startAngle);
    const y1 = radius - radius * Math.cos(startAngle);
    const x2 = radius + radius * Math.sin(endAngle);
    const y2 = radius - radius * Math.cos(endAngle);

    slices.push(`
      <path 
        d="M ${radius},${radius} 
           L ${x1},${y1} 
           A ${radius},${radius} 0 0 1 ${x2},${y2} 
           Z"
        fill="${FatoStateColor[point.key]}"
        stroke="black"
        stroke-width="1"
      />
    `);
  }

  return `<svg width="${diameter}" height="${diameter}" viewBox="0 0 ${diameter} ${diameter}" xmlns="http://www.w3.org/2000/svg">
    ${slices.join('')}
  </svg>`;
};

const svgToImage = (svgString: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    try {
      const base64 = btoa(decodeURIComponent(encodeURIComponent(svgString)));
      const img = new Image();

      img.onload = () => {
        resolve(img);
      };

      img.onerror = (err) => {
        console.error('IMAGE LOAD ERROR', err, ' SRC:', img.src);
        reject(new Error('IMAGE LOAD ERROR'));
      };

      img.src = `data:image/svg+xml;base64,${base64}`;
    } catch (e) {
      console.error('BASE64 / SVG ERROR', e);
      reject(new Error('BASE64 / SVG ERROR'));
    }
  });
};

export const useVertiportClusters = (points: PointFeature<GeoJsonProperties>[]): PointFeature<GeoJsonProperties>[] => {
  const { current: map } = useMap();
  const loadingImages = useRef<Set<string>>(new Set());

  const superClusterOptions: Supercluster.Options<GeoJsonProperties, VertiportClusterProps> = {
    radius: 38,
    minZoom: 0,
    maxZoom: 23,
    minPoints: 2,
    map: (props) => {
      const state = props?.value?.state;
      return {
        imageId: '',
        NOMINAL: state === FatoStateEnum.NOMINAL ? 1 : 0,
        INOPERABLE: state === FatoStateEnum.INOPERABLE ? 1 : 0,
        CONTINGENCY: state === FatoStateEnum.CONTINGENCY ? 1 : 0,
      };
    },
    reduce: (acc, props) => {
      acc.NOMINAL += props.NOMINAL;
      acc.INOPERABLE += props.INOPERABLE;
      acc.CONTINGENCY += props.CONTINGENCY;
    },
  };

  const { clusters } = useSuperCluster({ points, options: superClusterOptions });

  const clustersWithImage = clusters.map((cluster) => {
    if (cluster.properties) {
      const props = {
        NOMINAL: cluster.properties.NOMINAL || 0,
        INOPERABLE: cluster.properties.INOPERABLE || 0,
        CONTINGENCY: cluster.properties.CONTINGENCY || 0,
      };

      const key = `N${props.NOMINAL}-C${props.CONTINGENCY}-I${props.INOPERABLE}`;
      cluster.properties.imageId = `cluster-icon-${key}`;
    }

    return cluster;
  });

  useEffect(() => {
    if (!map) return;

    const loadClusterImages = async () => {
      const promises: Promise<void>[] = [];

      for (const cluster of clusters) {
        const props = cluster.properties;
        if (!props) continue;

        const key = `N${props.NOMINAL}-C${props.CONTINGENCY}-I${props.INOPERABLE}`;
        const imageId = `cluster-icon-${key}`;

        if (map.hasImage(imageId) || loadingImages.current.has(imageId)) continue;

        loadingImages.current.add(imageId);

        const svg = createPieByPoints({
          NOMINAL: props.NOMINAL,
          INOPERABLE: props.INOPERABLE,
          CONTINGENCY: props.CONTINGENCY,
          TOTAL: props.point_count || 1,
        });

        const promise = svgToImage(svg)
          .then((img) => {
            if (!map.hasImage(imageId)) map.addImage(imageId, img);
            props.imageId = imageId;
          })
          .catch((err) => {
            console.error('Error loading cluster image', err);
          })
          .finally(() => {
            loadingImages.current.delete(imageId);
          });

        promises.push(promise);
      }

      await Promise.all(promises);
    };

    void loadClusterImages();
  }, [map, clusters]);

  return clustersWithImage;
};
